<template>
  <div class="form">
    <div class="form__content">
      <h1 class="extra-large-headline">Source of funds document upload</h1>
      <p class="bodycopy">
        Please upload the following documents to verify your source of funds.
      </p>
      <ul class="form__document-instructions bodycopy">
        <li>Past 3 month’s bank statement.</li>
        <li>Recent payslips.</li>
      </ul>
    </div>
    <form @submit.prevent>
      <FormAlert
        text="Failure to upload proper documents to prove your source of funds will
      result in your order being put on hold and significantly delayed."
      />

      <MultiFileUploadField
        name="sofDocumentUpload"
        label="Please select all
      your documents in one go"
        placeholder="Select or drag your file"
        iconType="DocumentCircle"
        :errors="form.sofDocumentUploadErrors"
        @updateFiles="updateFiles"
      />

      <Checkbox
        name="tnc"
        text="I hereby declare that the supporting documents I have provided are true and complete."
        v-model:value="form.tnc"
        v-model:tnc="form.tnc"
        :errors="form.tncErrors"
      />

      <ButtonBase
        :isDisabled="formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        My selfie is clear and legible
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";

import useCustomerActions from "@/composables/api/useCustomerActions";
import useApiHelpers from "@/composables/api/helpers";

import MultiFileUploadField from "@/components/ui/inputs/MultiFileUploadField.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import useFormValidation from "@/composables/validations/useFormValidation";
import FormAlert from "@/components/ui/alerts/FormAlert.vue";

const router = useRouter();
const globalStore = useGlobalStore();
const { validateMultiFileInput, validateCheckboxField } = useFormValidation();
const { fetchCustomerId } = useCustomerActions();
const { postRequest } = useApiHelpers();

const form = ref({
  sofDocumentUpload: [],
  sofDocumentUploadErrors: "",
  tnc: false,
  tncErrors: "",
});
const updateFiles = (files) => {
  form.value.sofDocumentUpload = [];
  form.value.sofDocumentUpload = [...files];

  const fileValidationErrors = validateMultiFileInput(
    "sofDocumentUpload",
    form.value.sofDocumentUpload
  );

  form.value.sofDocumentUploadErrors = fileValidationErrors;
};

const formHasErrors = computed(() => {
  const anyErrors =
    form.value.sofDocumentUploadErrors !== "" || form.value.tncErrors !== "";
  const anyFieldsEmpty =
    form.value.sofDocumentUpload.length === 0 || form.value.tnc === false;
  return anyErrors || anyFieldsEmpty;
});

watch(
  () => form.value.tnc,
  (tnc) => {
    const tncErrors = validateCheckboxField("source of funds declaration", tnc);
    form.value.tncErrors = tncErrors;
  }
);

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const formData = new FormData();

  formData.set("documentReference", custId);
  form.value.sofDocumentUpload.forEach((doc) =>
    formData.append("documentFiles[]", doc)
  );

  formData.set("documentType", "sof_document");

  globalStore.setIsFormLoading(true);
  postRequest("documents", formData, () => router.push("/payment"));
};
</script>
<style lang="scss" scoped>
.form {
  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  &__document-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}
</style>
